import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ButtonText from '../components/button/button-text/ButtonText';
import CreateProjectButton from '../components/create-project-button/CreateProjectButton';
import { CLEAR_CLUBS, findOrganizationForOrganizationNumber } from './club-search-reducer';
import SearchResultItem from '../organization/organization-search/SearchResultItem';
import { performSelectionSearch } from '../project/project-actions';
import { intercomShowNewMessageRaw, useIntercomFunction } from '../support-and-tracking/intercom-actions';
import useDebounce from '../components/hooks/debounce';
import ThemeContainer from '../components/theme-container/ThemeContainer';
import { THEMES } from '../utils/cms-utils';
import ProjectCardList from '../project/project-card-list/ProjectCardList';
import ScreenReaderOnly from '../accessibility/ScreenReaderOnly';
import { searchForClubs, selectedClub } from './club-search-actions';
import { getOrganizationByOrganizationNumber } from '../organization/organization-actions';
import ErrorMessage from '../components/info-messages/ErrorMessage';
import { fetchEvent } from '../event/event-actions';
import { getEvent } from '../event/event-reducer';
import Spinner from '../components/spinner/Spinner';
import { organizationIsFetching } from '../organization/organizations-reducer';
import styles from './club-search.module.less';

const ClubSearch = ({
    shouldAutoFocus = false,
    isActive,
    hasRenderedResults,
    clubsClassName,
    templateId,
    eventId,
    inactiveNoResultsText = 'Det finnes ingen dugnader til denne klubben.',
    activeResultsText = 'Ønsker du å starte en ny dugnad til',
    activeNoResultsText = 'Det finnes ingen dugnader til denne klubben ennå. Start en nå!',
}) => {
    const dispatch = useDispatch();
    const searchInput = useRef(null);
    const searchResultsContainer = useRef(null);
    const [isSearchingProjects, setIsSearchingProjects] = useState(null);
    const [focused, setFocused] = useState(shouldAutoFocus);
    const [query, setQuery] = useState('');
    const [searchResult, setSearchResult] = useState([]);
    const [projectSearchError, setProjectSearchError] = useState();

    const selectedOrganization = useSelector(findOrganizationForOrganizationNumber);
    const isFetchingSelectedOrganization = useSelector(organizationIsFetching);

    const clubs = useSelector((state) => state.clubSearch.clubs);
    const isSearchingForClubs = useSelector((state) => state.clubSearch.isSearching);
    const organizationName = useSelector((state) => state.clubSearch.organizationName);
    const searchString = useSelector((state) => state.clubSearch.query);
    const clubSearchError = useSelector((state) => state.clubSearch.error);
    const event = useSelector((state) => getEvent(state, eventId));

    const createProjectParams = templateId ? `?templateId=${templateId}` : '?';
    const [linkParams, setLinkParams] = useState(createProjectParams);

    const isValidQuery = (query) => query?.length >= 3;

    const debouncedQuery = useDebounce(query, 400);

    const intercomShowNewMessage = useIntercomFunction(intercomShowNewMessageRaw);

    const isActiveIncludingEvent = isActive !== false && event?.active;

    useEffect(() => {
        if (selectedOrganization?.id) {
            setLinkParams(createProjectParams + `&organizationId=${selectedOrganization?.id}`);
        }
    }, [createProjectParams, selectedOrganization?.id]);

    useEffect(() => {
        if (eventId) {
            dispatch(fetchEvent(eventId));
        }
    }, [eventId]);

    useEffect(() => {
        if (isValidQuery(debouncedQuery)) {
            dispatch(searchForClubs(query));
        }
    }, [debouncedQuery]);

    useEffect(() => {
        if (!isValidQuery(query)) {
            dispatch({
                type: CLEAR_CLUBS,
            });
            setSearchResult([]);
        }
    }, [query]);

    useEffect(() => {
        if (hasRenderedResults) {
            hasRenderedResults(isValidQuery(query) && searchResult?.length > 0);
        }
    }, [query, searchResult?.length]);

    const handleInputChange = () => {
        setQuery(searchInput.current?.value);
    };

    const onKeyDown = (e) => {
        if ((e.key === 'ArrowDown' || e.key === 'Enter') && searchResultsContainer.current?.childNodes?.[0]) {
            e.preventDefault();
            searchResultsContainer.current?.childNodes[0].focus();
        }
    };

    const renderSearchResult = ({
        searchResult,
        organizationName,
        linkParams,
        isSearchingProjects,
        isActiveIncludingEvent,
    }) => {
        const Wrapper = ({ children }) => (
            <ThemeContainer mainColor={THEMES.GREY}>
                <div className={styles.searchResults}>
                    <div className={classNames(styles.projectsContainer, styles.projectsContainerWithResults)}>
                        {children}
                    </div>
                </div>
            </ThemeContainer>
        );
        if (isFetchingSelectedOrganization) {
            return (
                <Wrapper>
                    <Spinner />
                </Wrapper>
            );
        } else if (searchResult && searchResult.length > 0) {
            return (
                <Wrapper>
                    <ProjectCardList projectIds={searchResult.map((projectId) => projectId)} />
                    {isActiveIncludingEvent && (
                        <div className={styles.createProject}>
                            <p>
                                {activeResultsText} {organizationName}?
                            </p>
                            <CreateProjectButton linkParams={linkParams}>{`Start en dugnad`}</CreateProjectButton>
                            <p className={styles.toOrgName}>til {organizationName}</p>
                        </div>
                    )}
                </Wrapper>
            );
        } else if (!isSearchingProjects && organizationName) {
            return (
                <Wrapper>
                    <div className={styles.createProject}>
                        {isActiveIncludingEvent ? (
                            <>
                                <p className={styles.noResults}>{activeNoResultsText}</p>
                                <CreateProjectButton linkParams={linkParams}>{`Start en dugnad`}</CreateProjectButton>
                                <p className={styles.toOrgName}>til {organizationName}</p>
                            </>
                        ) : (
                            <p className={styles.noResults}>{inactiveNoResultsText}</p>
                        )}
                        {renderQuestionComponent()}
                    </div>
                </Wrapper>
            );
        }
    };

    const renderQuestionComponent = () => (
        <div className={styles.contactSpleisContainer}>
            Spørsmål?{' '}
            <ButtonText
                tightFocus
                className={styles.searchResultInfo}
                onClick={() => {
                    intercomShowNewMessage('');
                }}
            >
                Chat med oss
            </ButtonText>{' '}
            <i className="far fa-comment-dots" />
        </div>
    );

    const handleOrganizationSelection = (selection) => {
        setIsSearchingProjects(true);
        setProjectSearchError(null);
        dispatch(
            performSelectionSearch({
                organizationNumbers: [selection.value],
                eventIds: [eventId],
            })
        )
            .then((result) => {
                setSearchResult(result?.projectIds);
                dispatch(selectedClub({ organizationNumber: selection.value, organizationName: selection.label }));
                searchInput.current.value = selection.label;
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setProjectSearchError(err?.message || 'Noe gikk galt, vennligst prøv igjen');
                }
            })
            .finally(() => setIsSearchingProjects(false));
        dispatch(getOrganizationByOrganizationNumber(selection.value));
    };

    return (
        <section className={styles.searchSection}>
            <form autoComplete={'off'} onSubmit={(e) => e.preventDefault()}>
                <label className={classNames(focused && styles.focused, styles.searchFieldContainer)}>
                    {isSearchingForClubs ? (
                        <Spinner size="1rem" borderWidth="2px" className={styles.icon} />
                    ) : (
                        <i
                            className={classNames(
                                'fa-solid fa-magnifying-glass',
                                styles.icon,
                                (focused || query) && styles.focused
                            )}
                            aria-hidden
                        />
                    )}
                    <ScreenReaderOnly>Finn laget ditt</ScreenReaderOnly>
                    <input
                        autoFocus={shouldAutoFocus}
                        type="search"
                        name="search"
                        onChange={handleInputChange}
                        onBlur={() => {
                            setFocused(false);
                        }}
                        onFocus={() => setFocused(true)}
                        placeholder={'Finn klubben din'}
                        ref={searchInput}
                        onKeyDown={onKeyDown}
                        autoComplete={'off'}
                    />
                </label>
            </form>

            <>
                {isValidQuery(searchString) && (
                    <>
                        <div className={styles.searchResultsWrapper}>
                            <ThemeContainer mainColor={THEMES.WHITE}>
                                <ul
                                    ref={searchResultsContainer}
                                    className={[styles.searchResultContainer, clubsClassName].join(' ')}
                                >
                                    {clubs?.map((club) => (
                                        <SearchResultItem
                                            key={club.organizationNumber}
                                            name={club.name}
                                            organizationNumber={club.organizationNumber}
                                            handleOrganizationSelection={handleOrganizationSelection}
                                        />
                                    ))}
                                </ul>
                                {projectSearchError && (
                                    <ErrorMessage className={styles.errorMessage}>{projectSearchError}</ErrorMessage>
                                )}
                            </ThemeContainer>
                        </div>
                        {clubs?.length === 0 && !isSearchingForClubs && !organizationName ? (
                            <ThemeContainer mainColor={THEMES.WHITE}>
                                {clubSearchError?.global && (
                                    <ErrorMessage className={styles.errorMessage}>
                                        {clubSearchError?.global}
                                    </ErrorMessage>
                                )}
                                <div className={styles.noResultsContainer}>
                                    <div className={styles.noResults}>
                                        <p>
                                            Vi finner ikke klubben du søkte på. Er du sikker på at klubben er registrert
                                            i hos Norges idrettsforbund?
                                        </p>
                                        {renderQuestionComponent()}
                                    </div>
                                </div>
                            </ThemeContainer>
                        ) : null}
                    </>
                )}
                {renderSearchResult({
                    searchResult,
                    organizationName,
                    linkParams,
                    isSearchingProjects,
                    isActiveIncludingEvent,
                })}
            </>
        </section>
    );
};

export default ClubSearch;
